
import Component from 'vue-class-component';
import Vue from 'vue';
import { Prop, Watch } from 'vue-property-decorator';
import config from '@/config';
import { PlatformService, UserService, UserServiceProduct } from '@/services/merchantSpace/types';
import { User } from '@/services/userSpace/types';
import { SERVICE_TOOLTIP_IMAGES } from '@/constants';
import { addService, refreshUser, createExternalAccountIdRule } from '@/services/userSpace/UserService';
import { isPlatformType } from '@/services/merchantSpace/PlatformService';
import { isConnected } from '@/services/common/common';

@Component
export default class UserServiceDialog extends Vue {
  @Prop({ type: Object, required: false, default: undefined })
  readonly userService!: UserService | undefined;

  @Prop({ type: Object, required: false, default: undefined })
  readonly userServiceProduct!: UserServiceProduct | undefined;

  @Prop({ type: Object, required: true })
  readonly platformService!: PlatformService;

  signUpLabel: any = this.$store.getters['platform/getPlatformStyle'].signup;

  user: User = this.$store.getters['user/getUser'];

  newCode: string = this.textFieldValue();

  customTooltip: any = SERVICE_TOOLTIP_IMAGES[this.platformService.service.key];

  dialog: boolean = false;

  isLoading: boolean = false;

  canSave: boolean = false;

  showSuccessIcon: boolean = false;

  get platform() {
    return this.$store.getters['platform/getPlatform'];
  }

  get externalAccountIdRules() {
    const regex = this.platform.config.externalAccountIdRegex;
    return createExternalAccountIdRule(regex ? new RegExp(regex) : undefined);
  }

  getIcon(): string {
    if (this.showSuccessIcon) {
      return 'mdi-check-circle';
    }

    if (this.platformService.userCanAdd && !this.userService) {
      return 'mdi-plus-circle-outline';
    }

    if (this.platformService.userCanUpdate && this.userService && !this.platformService.service.emailAsExternal) {
      const activeProduct = this.getActiveUserServiceProduct(this.userService);
      const code = activeProduct ? activeProduct.productCode : this.userService.externalAccountId;
      if (this.isUuid(code)) return 'mdi-plus';
      return 'mdi-pencil-outline';
    }

    return '';
  }

  getTooltipText(): string {
    if (this.showSuccessIcon) {
      return 'Ajout réussi';
    }

    if (this.platformService.userCanAdd && !this.userService) {
      return 'Ajouter le service';
    }

    if (this.platformService.userCanUpdate && this.userService && !this.platformService.service.emailAsExternal) {
      const activeProduct = this.getActiveUserServiceProduct(this.userService);
      const code = activeProduct ? activeProduct.productCode : this.userService.externalAccountId;
      if (this.isUuid(code)) return 'Compléter mon identifiant';
      return 'Modifier mon identifiant';
    }

    return '';
  }

  isUuid(value: string | null): boolean {
    if (!value) return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }

  async toggleDialog() {
    this.isLoading = true;
    if (this.platformService.service.emailAsExternal && !this.userService) {
      await this.save(this.user.email);
      return;
    }
    this.dialog = !this.dialog;
    this.isLoading = false;
  }

  textFieldValue() {
    if (!this.userService) {
      return '';
    }

    if (this.platformService.userCanUpdate) {
      const activeUserServiceProduct = this.getActiveUserServiceProduct(this.userService);
      if (activeUserServiceProduct && this.isUuid(activeUserServiceProduct.productCode)) return '';
      return activeUserServiceProduct ? activeUserServiceProduct.productCode : '';
    }

    if (this.platformService.userCanAdd) {
      return this.userService.externalAccountId;
    }

    return '';
  }

  getActiveUserServiceProduct(userService: UserService): UserServiceProduct | null {
    const activeUserServiceProduct = userService.userServiceProducts.find(({ revoked }) => !revoked);
    return activeUserServiceProduct || null;
  }

  isRegexValid(): boolean {
    const regex = this.platform.config.externalAccountIdRegex;
    if (!regex) return true;
    return new RegExp(regex).test(this.newCode);
  }

  isSavable(): void {
    let state = false;

    if (!this.isRegexValid()) {
      this.canSave = false;
      return;
    }

    if (this.platformService.userCanAdd) {
      if (this.newCode) state = true;
      this.canSave = state;
      return;
    }

    if (this.platformService.userCanUpdate) {
      if (this.userService) {
        const activeUserServiceProduct = this.getActiveUserServiceProduct(this.userService);
        state = activeUserServiceProduct ? this.newCode !== activeUserServiceProduct.productCode : false;
      }
    }

    this.canSave = state;
  }

  get help() {
    return { name: 'help' };
  }

  async save(newCode: string) {
    this.isLoading = true;
    this.dialog = false;
    await addService(this.platformService.serviceId, newCode)
      .then(async () => {
        await refreshUser().then(() => {
          this.$emit('saveSuccess');
          this.showSuccessIcon = true;
          setTimeout(() => {
            this.showSuccessIcon = false;
          }, 5000);
        });
      })
      .catch(() => {
        this.$emit('saveFailed');
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  get cardTooltipImageSrc() {
    return this.signUpLabel.cardTooltipImageUrl ? `${config.apiUrl}/api${this.signUpLabel.cardTooltipImageUrl}` : null;
  }

  get cardTooltip() {
    if (this.customTooltip && this.customTooltip.mainDesc) {
      return {
        mainDesc: this.customTooltip.mainDesc,
        optionalDesc: this.customTooltip.optionalDesc,
        imageSrc: this.customTooltip.imageSrc,
      };
    }

    if (isPlatformType(this.platformService.service.typeId) && this.signUpLabel.cardTooltipMainDesc) {
      return {
        mainDesc: this.signUpLabel.cardTooltipMainDesc,
        optionalDesc: this.signUpLabel.cardTooltipOptionalDesc,
        imageSrc: this.cardTooltipImageSrc,
      };
    }

    return null;
  }
}
